import { Inject, Injectable } from '@angular/core';
import { Auth, User as FireAuthUser } from 'firebase/auth';
import { AUTH } from '../firebase-injection';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { User } from '../models/user.model';
import { LocalStorageService } from './local-storage.service';
import { LocaleSwitchService } from './locale-switch.service';
import { UserService } from './user.service';
import { needsEmailVerification } from './auth.utils';

@Injectable({
  providedIn: 'root',
})
export class LoginActionsService {
  private isAuthStateChangeExecuted = false;
  private currentUser: BehaviorSubject<User | undefined>;

  constructor(
    @Inject(AUTH) private auth: Auth,
    private userService: UserService,
    private localStorage: LocalStorageService,
    private router: Router,
    private localeSwitch: LocaleSwitchService
  ) {
    this.currentUser = new BehaviorSubject<User | undefined>(undefined);
  }

  public getCurrentUser() {
    if (!this.isAuthStateChangeExecuted) {
      this.activateLoginActions();
    }
    return this.currentUser;
  }

  private activateLoginActions() {
    this.updateUserInLocalStorage(this.auth.currentUser || undefined);
    this.isAuthStateChangeExecuted = true;
    this.auth.onAuthStateChanged((fireAuthUser) => {
      if (fireAuthUser) {
        this.updateUserInLocalStorage(fireAuthUser);
        if (needsEmailVerification(fireAuthUser)) {
          this.router.navigate(['/verify-email']);
          return;
        }
      } else {
        this.logout();
      }
    });
  }

  public logout() {
    this.auth.signOut().then(() => {
      this.localStorage.clear();
      this.currentUser.next(undefined);
      this.router.navigate(['/landing']);
    });
  }

  private updateUserInLocalStorage(fireAuthUser: FireAuthUser | undefined) {
    if (!fireAuthUser) {
      this.localStorage.setUser(undefined);
      this.currentUser.next(undefined);
      return;
    }
    this.userService.getUser(fireAuthUser.uid).then((user) => {
      if (!user) {
        const newUser = new User(
          fireAuthUser.uid,
          fireAuthUser.displayName ?? undefined,
          undefined,
          fireAuthUser.email ?? undefined,
          fireAuthUser.phoneNumber ?? undefined,
          fireAuthUser.photoURL ?? undefined,
          false,
          0,
          undefined,
          undefined,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          false,
          this.localeSwitch.getActiveLocale()
        );
        this.userService.addUser(newUser).then(() => {
          this.localStorage.setUser(newUser);
          this.currentUser.next(newUser);
        });
        return;
      }
      if (user.email !== fireAuthUser.email || !user.preferredLanguage) {
        user.email = fireAuthUser.email || user.email;
        user.preferredLanguage =
          user.preferredLanguage || this.localeSwitch.getActiveLocale();
        this.userService.updateUser(user).then(() => {
          this.localStorage.setUser(user);
          this.currentUser.next(user);
        });
        return;
      } else {
        this.localStorage.setUser(user);
        this.currentUser.next(user);
        return;
      }
    });
  }
}
